/**
 * Cartographic layout document — page composition, not map chrome.
 * Units on the page are millimetres unless noted.
 */

export type PageUnits = 'mm' | 'in';
export type PageOrientation = 'portrait' | 'landscape';
export type IsoPageName = 'a5' | 'a4' | 'a3' | 'a2' | 'a1' | 'a0';
export type AnsiPageName = 'letter' | 'legal' | 'tabloid' | 'ansi-c' | 'ansi-d' | 'ansi-e';
export type ArchitecturalPageName =
  | 'architectural-a'
  | 'architectural-b'
  | 'architectural-c'
  | 'architectural-d'
  | 'architectural-e';
export type PagePresetId =
  | `${IsoPageName}-${PageOrientation}`
  | `${AnsiPageName}-${PageOrientation}`
  | `${ArchitecturalPageName}-${PageOrientation}`
  | 'custom';

export interface PageRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** A page-local coordinate. Graphic coordinates are normalized to their frame. */
export interface LayoutGraphicPoint {
  readonly x: number;
  readonly y: number;
}

export interface LayoutGraphicRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface LayoutGraphicPath {
  readonly points: readonly LayoutGraphicPoint[];
  readonly closed: boolean;
}

export interface LayoutGraphicPointGeometry {
  readonly type: 'point';
  readonly point: LayoutGraphicPoint;
}

export interface LayoutGraphicRectGeometry {
  readonly type: 'rect';
  readonly rect: LayoutGraphicRect;
}

export interface LayoutGraphicPathGeometry {
  readonly type: 'path';
  readonly paths: readonly LayoutGraphicPath[];
}

export type LayoutGraphicGeometry =
  | LayoutGraphicPointGeometry
  | LayoutGraphicRectGeometry
  | LayoutGraphicPathGeometry;

export type LayoutTextGraphicType =
  | 'rectangleText'
  | 'polygonText'
  | 'straightText'
  | 'curvedText'
  | 'circleText'
  | 'ellipseText';
export type LayoutLineGraphicType = 'line' | 'curvedLine' | 'freehandLine' | 'arc';
export type LayoutPointGraphicType = 'point';
export type LayoutPolygonGraphicType =
  | 'rectangle'
  | 'polygon'
  | 'circle'
  | 'ellipse'
  | 'lasso'
  | 'roundedRectangle'
  | 'triangle'
  | 'rightTriangle'
  | 'halfCircle'
  | 'cross'
  | 'x'
  | 'cloud';
export type LayoutArrowGraphicType =
  | 'arrow'
  | 'curvedArrow'
  | 'doubleArrow'
  | 'curvedDoubleArrow';
export type LayoutGraphicType =
  | LayoutTextGraphicType
  | LayoutLineGraphicType
  | LayoutPointGraphicType
  | LayoutPolygonGraphicType
  | LayoutArrowGraphicType;

export interface LayoutGraphicStroke {
  readonly color: string | null;
  readonly widthPt: number;
  readonly style: 'solid' | 'dash' | 'dot' | 'dashDot';
  readonly cap: 'butt' | 'round' | 'square';
  readonly join: 'miter' | 'round' | 'bevel';
  readonly opacityPercent: number;
}

export interface LayoutGraphicFill {
  readonly color: string | null;
  readonly opacityPercent: number;
  readonly rule: 'nonzero' | 'evenodd';
}

export interface LayoutGraphicMarker {
  readonly shape: 'circle' | 'square' | 'diamond' | 'triangle' | 'cross' | 'x';
  readonly sizePt: number;
  readonly fill: LayoutGraphicFill;
  readonly stroke: LayoutGraphicStroke;
}

export interface LayoutGraphicArrowheads {
  readonly start: 'none' | 'open' | 'closed';
  readonly end: 'none' | 'open' | 'closed';
  readonly sizePt: number;
}

export interface LayoutGraphicShapeParams {
  readonly cornerRadius: number;
  readonly innerRatio: number;
  readonly cloudLobes: number;
  readonly arcStartDeg: number;
  readonly arcEndDeg: number;
  readonly tension: number;
}

export interface LayoutGraphicTextSettings {
  readonly text: string;
  readonly overflow: 'clip' | 'shrink' | 'visible';
  readonly columnCount: number;
  readonly columnGapPt: number;
  readonly marginPt: number;
  readonly pathOffset: number;
  readonly reversePath: boolean;
}

export interface LayoutPage {
  readonly widthMm: number;
  readonly heightMm: number;
  readonly units: PageUnits;
  readonly presetId: PagePresetId;
}

export interface MapFrameCamera {
  readonly center: readonly [number, number];
  readonly zoom: number;
  readonly bearing: number;
  readonly scaleDenominator: number | null;
}

export interface MapFrameElement {
  readonly id: string;
  readonly type: 'mapFrame';
  readonly rect: PageRect;
  readonly camera: MapFrameCamera;
  readonly lockScale: boolean;
  readonly source: 'active-map';
}

export type NorthArrowStyleId =
  | 'arcgisNorth1'
  | 'arcgisNorth2'
  | 'arcgisNorth3'
  | 'needle'
  | 'flag'
  | 'survey'
  | 'marker'
  | 'slender'
  | 'solid'
  | 'ring'
  | 'compass'
  | 'compassRose'
  | 'starRose'
  | 'fourPoint'
  | 'quadrantRose'
  | 'ornateCircle'
  | 'nauticalRose'
  | 'blockArrow'
  | 'splitBlockArrow'
  | 'simpleCompass'
  | 'trueNorth'
  | 'magneticNorth1'
  | 'magneticNorth2'
  | 'gridNorth'
  | 'simpleCircleFilled'
  | 'simpleCircleOutline'
  | 'detailedRose';
export type NorthArrowOrientation = 'map' | 'true' | 'magnetic' | 'grid';
export type ScaleBarStyleId =
  | 'scaleLine1'
  | 'scaleLine2'
  | 'scaleLine3'
  | 'stepped'
  | 'alternating1'
  | 'alternating2'
  | 'doubleAlternating1'
  | 'doubleAlternating2'
  | 'hollow1'
  | 'hollow2'
  | 'singleDivision'
  | 'dualFeetMetersLineLeft'
  | 'dualMilesKilometersLine'
  | 'dualMilesKilometersLineLeft'
  | 'dualMilesKilometersBar'
  | 'dualMilesKilometersBarLeft'
  | 'dualFeetMetersBarLeft'
  | 'dualFeetMetersBarRight'
  | 'dualFeetMetersLineRight';
export type ScaleBarUnits = 'metric' | 'imperial' | 'dual';
export type ScaleBarDistanceUnit = 'meters' | 'kilometers' | 'feet' | 'miles';
export type ScaleBarFittingStrategy =
  | 'adjustDivision'
  | 'adjustDivisions'
  | 'adjustDivisionAndDivisions'
  | 'adjustWidth'
  | 'fixedBarWidth';

export interface ScaleBarUnitSettings {
  readonly unit: ScaleBarDistanceUnit;
  /** Ground distance in `unit` per major division; null means automatic. */
  readonly divisionValue: number | null;
  readonly divisions: number;
  readonly subdivisions: number;
}

export interface LayoutTextSymbol {
  readonly color: string;
  readonly sizePt: number;
  readonly fontFamily: string;
  readonly fontStyle: 'regular' | 'italic' | 'bold' | 'boldItalic';
  readonly outlineColor: string;
  readonly outlineWidthPt: number;
  readonly underline: boolean;
  readonly strikethrough: boolean;
  readonly textCase: 'normal' | 'uppercase' | 'lowercase' | 'smallCaps';
  readonly positionAdjustment: 'normal' | 'superscript' | 'subscript';
  readonly horizontalAlignment: 'left' | 'center' | 'right' | 'justify';
  readonly verticalAlignment: 'top' | 'middle' | 'baseline' | 'bottom';
  readonly offsetXPt: number;
  readonly offsetYPt: number;
  readonly angle: number;
  readonly halo: {
    readonly enabled: boolean;
    readonly color: string;
    readonly sizePt: number;
  };
  readonly shadow: {
    readonly enabled: boolean;
    readonly color: string;
    readonly offsetXPt: number;
    readonly offsetYPt: number;
  };
  readonly callout: {
    readonly enabled: boolean;
    readonly fillColor: string;
    readonly outlineColor: string;
    readonly paddingPt: number;
  };
  readonly opacityPercent: number;
}

export interface LayoutGraphicElementBase {
  readonly id: string;
  readonly type: 'graphic';
  /** Static page graphics are deliberately not owned by a map frame. */
  readonly mapFrameId?: undefined;
  readonly graphicType: LayoutGraphicType;
  readonly rect: PageRect;
  /** Clockwise page rotation in degrees. */
  readonly rotation: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly name: string;
  readonly visible: boolean;
  readonly locked: boolean;
  readonly geometry: LayoutGraphicGeometry;
}

export interface LayoutTextGraphicElement extends LayoutGraphicElementBase {
  readonly category: 'text';
  readonly graphicType: LayoutTextGraphicType;
  readonly text: LayoutGraphicTextSettings;
  readonly textSymbol: LayoutTextSymbol;
  readonly stroke: LayoutGraphicStroke;
  readonly fill: LayoutGraphicFill;
  readonly shape: LayoutGraphicShapeParams;
}

export interface LayoutLineGraphicElement extends LayoutGraphicElementBase {
  readonly category: 'line';
  readonly graphicType: LayoutLineGraphicType;
  readonly stroke: LayoutGraphicStroke;
  readonly arrowheads: LayoutGraphicArrowheads;
  readonly shape: LayoutGraphicShapeParams;
}

export interface LayoutPointGraphicElement extends LayoutGraphicElementBase {
  readonly category: 'point';
  readonly graphicType: LayoutPointGraphicType;
  readonly marker: LayoutGraphicMarker;
}

export interface LayoutPolygonGraphicElement extends LayoutGraphicElementBase {
  readonly category: 'polygon';
  readonly graphicType: LayoutPolygonGraphicType;
  readonly stroke: LayoutGraphicStroke;
  readonly fill: LayoutGraphicFill;
  readonly shape: LayoutGraphicShapeParams;
}

export interface LayoutArrowGraphicElement extends LayoutGraphicElementBase {
  readonly category: 'arrow';
  readonly graphicType: LayoutArrowGraphicType;
  readonly stroke: LayoutGraphicStroke;
  readonly fill: LayoutGraphicFill;
  readonly arrowheads: LayoutGraphicArrowheads;
  readonly shape: LayoutGraphicShapeParams;
}

export type LayoutGraphicElement =
  | LayoutTextGraphicElement
  | LayoutLineGraphicElement
  | LayoutPointGraphicElement
  | LayoutPolygonGraphicElement
  | LayoutArrowGraphicElement;

/** Compatibility name retained for scale-bar call sites. */
export type ScaleBarTextSymbol = LayoutTextSymbol;

/**
 * ArcGIS Pro angle alignment. `map` keeps the symbol pinned to the map's
 * north, so it counter-rotates with the frame; `display` pins it to the page.
 */
export type SymbolAngleAlignment = 'display' | 'map';

export interface NorthArrowHalo {
  readonly enabled: boolean;
  readonly color: string;
  readonly outlineColor: string;
  /** Halo thickness in symbol points (the symbol box is 80 pt tall). */
  readonly sizePt: number;
}

export type ShapeMarkerFormId =
  | 'circle'
  | 'square'
  | 'triangle'
  | 'roundedSquare'
  | 'pin'
  | 'diamond'
  | 'star'
  | 'cross'
  | 'verticalBar'
  | 'horizontalBar'
  | 'capsule'
  | 'hexagon'
  | 'wideHexagon'
  | 'oval'
  | 'numberOne'
  | 'circledOne'
  | 'corner';

export type ShapeMarkerSource = 'style' | 'form' | 'font' | 'file';

/** One editable ArcGIS-style shape-marker layer. */
export interface NorthArrowMarkerLayer {
  readonly visible: boolean;
  readonly source: ShapeMarkerSource;
  readonly formId: ShapeMarkerFormId;
  readonly fontGlyph: string;
  readonly fileDataUrl: string | null;
  readonly fileName: string | null;
  readonly fillColor: string | null;
  readonly outlineColor: string | null;
  readonly outlineWidthPt: number;
  readonly respectFrame: boolean;
  readonly scaleProportionally: boolean;
}

export interface NorthArrowSymbol {
  /** `null` is the palette's "No color": strokes still draw, fills do not. */
  readonly color: string | null;
  /** Extra symbol rotation in degrees, clockwise on the page. */
  readonly angle: number;
  readonly angleAlignment: SymbolAngleAlignment;
  readonly marker: NorthArrowMarkerLayer;
  readonly halo: NorthArrowHalo;
}

export interface NorthArrowElement {
  readonly id: string;
  readonly type: 'northArrow';
  readonly mapFrameId: string;
  readonly styleId: NorthArrowStyleId;
  readonly symbol: NorthArrowSymbol;
  readonly rect: PageRect;
  readonly rotationFollowsFrame: true;
  readonly name: string;
  readonly visible: boolean;
  readonly locked: boolean;
  /** ArcGIS-style larger-scale threshold; null means no threshold. */
  readonly maximumScaleDenominator: number | null;
  /** ArcGIS-style smaller-scale threshold; null means no threshold. */
  readonly minimumScaleDenominator: number | null;
  readonly orientation: NorthArrowOrientation;
  /** Positive values rotate counterclockwise, matching ArcGIS Pro. */
  readonly calibrationAngle: number;
}

export interface ScaleBarElement {
  readonly id: string;
  readonly type: 'scaleBar';
  readonly mapFrameId: string;
  readonly styleId: ScaleBarStyleId;
  readonly units: ScaleBarUnits;
  readonly name: string;
  readonly visible: boolean;
  readonly locked: boolean;
  readonly computeAtCenter: boolean;
  readonly upper: ScaleBarUnitSettings;
  readonly lower: ScaleBarUnitSettings | null;
  readonly horizontalOffsetPt: number;
  readonly verticalOffsetPt: number;
  readonly fittingStrategy: ScaleBarFittingStrategy;
  readonly fixedBarWidthMm: number;
  readonly textSymbol: ScaleBarTextSymbol;
  readonly rect: PageRect;
}

export type LegendStyleId =
  | 'centerTitle'
  | 'grayBackground'
  | 'blackBorder'
  | 'manualColumns'
  | 'transparentBackground'
  | 'titleTextAligned'
  | 'largeText'
  | 'titleLargeTextAligned'
  | 'layerNamesLargeText'
  | 'scaledPatchesMediumText'
  | 'headingsMediumText'
  | 'titleMediumText';

export type LegendFittingStrategy =
  | 'adjustFontSize'
  | 'adjustColumns'
  | 'adjustColumnsAndFont'
  | 'adjustFrame'
  | 'manualColumns';

export type LegendTextPart =
  | 'title'
  | 'groupLayerName'
  | 'layerName'
  | 'heading'
  | 'label'
  | 'description';

export type LegendItemType = 'default' | 'horizontal' | 'vertical';

export type LegendItemArrangement =
  | 'patchLabelDescription'
  | 'patchDescriptionLabel'
  | 'labelPatchDescription'
  | 'labelDescriptionPatch'
  | 'descriptionPatchLabel'
  | 'descriptionLabelPatch';

export interface LegendFrameSymbol {
  readonly enabled: boolean;
  readonly color: string;
  readonly outlineColor: string;
  readonly outlineWidthPt: number;
  readonly gapXPt: number;
  readonly gapYPt: number;
  readonly offsetXPt: number;
  readonly offsetYPt: number;
}

export interface LegendSpacing {
  readonly titleGapPt: number;
  readonly groupLayerNameGapPt: number;
  readonly layerNameGapPt: number;
  readonly headingGapPt: number;
  readonly classGapPt: number;
  readonly patchLabelGapPt: number;
  readonly columnGapPt: number;
}

export type LegendTextSymbols = Readonly<Record<LegendTextPart, LayoutTextSymbol>>;

export interface LegendElement {
  readonly id: string;
  readonly type: 'legend';
  readonly mapFrameId: string;
  readonly styleId: LegendStyleId;
  readonly name: string;
  readonly visible: boolean;
  readonly locked: boolean;
  readonly rect: PageRect;
  readonly layerIds?: readonly string[];
  readonly title: string;
  readonly showTitle: boolean;
  readonly fittingStrategy: LegendFittingStrategy;
  readonly columnCount: number;
  readonly equalColumnWidths: boolean;
  readonly minimumFontSizePt: number;
  readonly wrapLabels: boolean;
  readonly labelWrapWidthPt: number;
  readonly wrapDescriptions: boolean;
  readonly descriptionWrapWidthPt: number;
  readonly spacing: LegendSpacing;
  readonly background: LegendFrameSymbol;
  readonly border: LegendFrameSymbol;
  readonly shadow: LegendFrameSymbol;
  readonly textSymbols: LegendTextSymbols;
  readonly itemOverrides?: Readonly<Record<string, LegendItemOverride>>;
}

export type TableFrameStyleId =
  | 'grayDoubleAlternatingRows'
  | 'blueUnderlineAlternatingRows'
  | 'blackBorderBoldHeading'
  | 'blueBackgroundWhiteBorder'
  | 'blueAlternatingRows'
  | 'grayAlternatingRows'
  | 'orangeBackground'
  | 'orangeAlternatingRows'
  | 'grayBackground';

export type TableFrameFittingStrategy =
  | 'adjustColumns'
  | 'adjustFontSize'
  | 'adjustColumnsAndFont'
  | 'adjustFrame';

export type TableFrameQuery = 'all' | 'visibleExtent' | 'selected';
export type TableFieldAlignment = 'left' | 'center' | 'right';
export type TableFieldSortDirection = 'none' | 'ascending' | 'descending';

export interface TableFrameField {
  readonly name: string;
  readonly alias: string;
  readonly visible: boolean;
  readonly widthPt: number;
  readonly alignment: TableFieldAlignment;
  readonly headingAlignment: TableFieldAlignment;
  readonly wrapHeading: boolean;
  readonly wrapValues: boolean;
  readonly numberFormat: 'automatic' | 'integer' | 'decimal' | 'percent';
  readonly decimalPlaces: number;
  readonly sortDirection: TableFieldSortDirection;
  readonly sortOrder: number;
}

export interface TableFrameElement {
  readonly id: string;
  readonly type: 'tableFrame';
  readonly mapFrameId: string;
  readonly sourceLayerId: string | null;
  readonly styleId: TableFrameStyleId;
  readonly name: string;
  readonly visible: boolean;
  readonly locked: boolean;
  readonly query: TableFrameQuery;
  readonly maximumRows: number;
  readonly fittingStrategy: TableFrameFittingStrategy;
  readonly minimumFontSizePt: number;
  readonly balanceColumns: boolean;
  readonly frameColumnGapPt: number;
  readonly showHeading: boolean;
  readonly fields: readonly TableFrameField[];
  readonly headingTextSymbol: LayoutTextSymbol;
  readonly rowTextSymbol: LayoutTextSymbol;
  readonly frame: LegendFrameSymbol;
  readonly headingBackgroundColor: string;
  readonly headingBorderColor: string;
  readonly headingUnderlineColor: string;
  readonly rowBorderColor: string;
  readonly rowBackground1: string;
  readonly rowBackground2: string;
  readonly rowBackground1Count: number;
  readonly rowBackground2Count: number;
  readonly rect: PageRect;
}

/** Per-layout label overrides; source map/layer metadata remains unchanged. */
export interface LegendItemOverride {
  readonly label?: string;
  readonly minLabel?: string;
  readonly maxLabel?: string;
  readonly visible?: boolean;
  readonly showGroupLayerName?: boolean;
  readonly showLayerName?: boolean;
  readonly showHeading?: boolean;
  readonly showLabel?: boolean;
  readonly showDescription?: boolean;
  readonly showFeatureCount?: boolean;
  readonly onlyVisibleInExtent?: boolean;
  readonly type?: LegendItemType;
  readonly arrangement?: LegendItemArrangement;
  readonly descriptionsAbove?: boolean;
  readonly keepInSingleColumn?: boolean;
  readonly patchWidthPt?: number;
  readonly patchHeightPt?: number;
  readonly scaleToPatch?: boolean;
  readonly layerNameIndentPt?: number;
  readonly headingIndentPt?: number;
  readonly classIndentPt?: number;
  readonly manualColumn?: number;
  readonly reverseRamp?: boolean;
}

export type LayoutPictureSourceKind = 'file' | 'url';

export interface LayoutPictureSource {
  readonly kind: LayoutPictureSourceKind;
  readonly name: string;
  readonly href: string | null;
  readonly dataUrl: string;
  readonly mimeType: string;
  readonly naturalWidthPx: number;
  readonly naturalHeightPx: number;
}

/**
 * Static layout picture. ArcGIS Pro stores the pixels with the project and
 * disconnects from the original file; GeoViewer does the same via `dataUrl`.
 */
export interface LayoutPictureElement {
  readonly id: string;
  readonly type: 'picture';
  readonly mapFrameId?: undefined;
  readonly name: string;
  readonly visible: boolean;
  readonly locked: boolean;
  readonly rect: PageRect;
  readonly rotation: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly preserveAspectRatio: boolean;
  readonly opacityPercent: number;
  readonly source: LayoutPictureSource;
  readonly background: LegendFrameSymbol;
  readonly border: LegendFrameSymbol;
  readonly shadow: LegendFrameSymbol;
}

export type LayoutSurroundElement =
  | NorthArrowElement
  | ScaleBarElement
  | LegendElement
  | TableFrameElement;

export type LayoutElement =
  | MapFrameElement
  | LayoutSurroundElement
  | LayoutGraphicElement
  | LayoutPictureElement;

export interface LayoutDocument {
  readonly id: string;
  readonly page: LayoutPage;
  readonly elements: readonly LayoutElement[];
}

export type LayoutWorkspaceView = 'map' | 'layout' | 'split';

/** 2D engine the layout page composes with, independent of the map tab's engine. */
export type LayoutFrameEngine = 'maplibre' | 'openlayers';

export interface LegendLayerItem {
  readonly id: string;
  readonly label: string;
  readonly kind: string;
  readonly groupLayerName?: string;
  readonly heading?: string;
  readonly description?: string;
  readonly featureCount?: number;
  readonly supportsVisibleExtent?: boolean;
  readonly patchColor?: string;
  readonly rasterRamp?: {
    readonly cssGradient: string;
    readonly minLabel: string;
    readonly maxLabel: string;
  };
}

export interface TableFrameRuntimeData {
  readonly layerId: string;
  readonly fields: readonly string[];
  readonly rows: readonly Readonly<Record<string, unknown>>[];
  readonly loading: boolean;
  readonly error: string | null;
}

export const DEFAULT_MAP_FRAME_ID = 'map-frame-1';
